import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import * as Re from 'remeda';
import * as R from 'ramda';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { JobMatchStatus } from '../../API';
import usePromiseWaiting from '../../hooks/usePromiseWaiting';
import useTechSkills from '../../hooks/useTechSkills';

import { listJobMatchesByEngineer } from '../../models/job-matches';

import routes from '../../routes';
import { useEngineer } from '../EngineerProvider';
import ErrorDisplay from '../ErrorDisplay';
import LoadingPage from '../Loading/Page';
import Page from '../Page';
import ActiveMatchCard from './ActiveMatchCard';

const useStyles = makeStyles((theme) => ({
  centered: {
    textAlign: 'center',
  },
  container: {
    marginTop: theme.spacing(2),
  },
}));

export default function ActiveJobs() {
  const classes = useStyles();
  const { engineer, fetching: fetchingEngineer } = useEngineer();
  const { skills, fetchingSkills } = useTechSkills();
  const [loadingMatches, jobMatches, errorMatches] = usePromiseWaiting(
    () => (engineer?.id ? listJobMatchesByEngineer(engineer.id, true) : null),
    [engineer?.id],
  );
  const [inactiveMatches, activeMatches] = useMemo(() => Re.pipe(
    jobMatches || [],
    R.partition((jobMatch) => jobMatch.status === JobMatchStatus.NO_GO),
    Re.map(R.sortBy((jm) => jm.openPosition.company.name)),
  ), [jobMatches]);
  let content = null;
  if (fetchingEngineer || loadingMatches || fetchingSkills) {
    content = <LoadingPage />;
  } else if (errorMatches) {
    content = <ErrorDisplay error="There was an error loading your active matches" showReload />;
  } else if (!jobMatches?.length) {
    content = (
      <Alert
        severity="warning"
        action={(
          <Button
            className={classes.centered}
            to={routes.jobs.potential}
            component={RouterLink}
            color="inherit"
            size="small"
          >
            Go to potential matches
          </Button>
        )}
      >
        <Typography>
          You don&apos;t have active matches at the moment.
          Go check the potential matches page to see if there is anything interesting for you
        </Typography>
      </Alert>
    );
  } else {
    content = (
      <>
        {Boolean(activeMatches?.length) && (
          <>
            <Typography variant="h4">Active Matches</Typography>
            {activeMatches.map((match) => (
              <ActiveMatchCard key={match.id} match={match} skills={skills} />))}
          </>
        )}
        {Boolean(inactiveMatches?.length) && (
          <>
            <Typography variant="h4">Inactive Matches</Typography>
            {inactiveMatches.map((match) => (
              <ActiveMatchCard key={match.id} match={match} skills={skills} />))}
          </>
        )}
      </>
    );
  }

  return (
    <Page
      title="Jobs"
      action={<Button variant="text" component={RouterLink} to={routes.jobs.potential}>Go to Potential Matches</Button>}
    >
      <Box className={classes.container}>
        {content}
      </Box>
    </Page>
  );
}
